import type { SearchHit } from '@contracts';
import { DEVICE_REMOVAL_REMOTE_UNAVAILABLE_MESSAGE } from '@api/devices/removal-errors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { KeyRound, X } from 'lucide-react';
import { Dialog } from 'radix-ui';
import { useNavigate } from 'react-router';
import { ApiError, isRemovalRemoteUnavailable, isStatus, removeDevice, removeDeviceOptions } from '../lib/api';
import { describeKeyError, signWithKey } from '../lib/webauthn';
import { useScope } from '../scope/scope';
import { Button } from '../ui/Button';
import { useToast } from '../ui/toast';

/**
 * Removes a device from WITTCO RMM (WRMM-70), after saying exactly what that does. It uninstalls WITTCO Remote from the
 * device, so like any device change it is approved with the person's security key, for this device.
 */
export function RemoveDeviceDialog({ device, open, onOpenChange }: { device: SearchHit; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { mspId } = useScope();
  const client = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const remove = useMutation({
    mutationFn: async () => removeDevice(mspId, device.id, await signWithKey(await removeDeviceOptions(mspId, device.id))),
    onSuccess: removal => {
      onOpenChange(false);
      void client.invalidateQueries({ predicate: q => q.queryKey[0] === 'scope' && q.queryKey[1] === mspId });
      const jobs = (removal.jobsCanceled ?? 0) + (removal.runsUnknown ?? 0), sessions = removal.sessionsEnded ?? 0;
      const ended = [jobs > 0 && `${jobs} open job${jobs === 1 ? '' : 's'} ended`,
        sessions > 0 && `${sessions} remote session${sessions === 1 ? '' : 's'} ended`].filter(Boolean).join(', ');
      toast({ title: `Removed ${removal.hostname}`, body: ended ? `${ended}. Its history stays in the audit trail.` : 'Its history stays in the audit trail.' });
      navigate(`/w/${mspId}/devices`);
    },
    onError: error => {
      // The server couldn't tell whether the removal finished (an error after it may have committed): look again.
      if (error instanceof ApiError && error.status >= 500 && !isRemovalRemoteUnavailable(error)) {
        void client.invalidateQueries({ predicate: q => q.queryKey[0] === 'scope' && q.queryKey[1] === mspId });
        onOpenChange(false);
        toast({ tone: 'warning', title: 'The removal may not have finished', body: 'The server couldn’t confirm it. Refresh the device list to see whether it’s gone, and try again if it isn’t.' });
        return;
      }
      toast({ tone: 'warning', title: 'The device wasn’t removed', body: !(error instanceof ApiError) ? describeKeyError(error)
      : isStatus(error, 409) ? 'Register a security key in Settings first: removing a device is approved with one.'
      : isStatus(error, 403) ? 'Removing devices needs permission to add devices for this site, and your security key.'
      : isRemovalRemoteUnavailable(error) ? DEVICE_REMOVAL_REMOTE_UNAVAILABLE_MESSAGE
      : isStatus(error, 404) ? 'It has already been removed.' : 'Try again.' });
    },
  });
  return (
    <Dialog.Root open={open} onOpenChange={o => { if (!remove.isPending) onOpenChange(o); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 animate-fade" />
        <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 max-h-[90dvh] overflow-y-auto rounded-t-2xl border border-line bg-raised p-5 shadow-pop animate-pop sm:inset-auto sm:left-1/2 sm:top-[10vh] sm:w-[min(520px,calc(100vw-2rem))] sm:-translate-x-1/2 sm:rounded-2xl">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-[17px] font-semibold">Remove {device.title}?</Dialog.Title>
              <Dialog.Description className="mt-0.5 text-[13px] text-ink-2">
                It leaves the device list and searches{device.organizationName ? ` for ${device.organizationName}` : ''}. Its history stays in the audit trail.
              </Dialog.Description>
            </div>
            <Dialog.Close className="rounded-md p-1 text-ink-3 hover:bg-hover" aria-label="Close"><X className="size-4" /></Dialog.Close>
          </div>
          <ul className="mt-4 list-disc space-y-1.5 pl-5 text-[13.5px] text-ink-2">
            {device.managed && <li>Its agent can’t report or take jobs again. Jobs not finished yet are canceled.</li>}
            {device.managed && <li>Remote sessions on it end. WITTCO Remote is uninstalled from it now if it’s online, or when it next connects.</li>}
            <li>To manage it again, uninstall the WITTCO agent on it and install it again with an installer from Settings.</li>
          </ul>
          {isRemovalRemoteUnavailable(remove.error) && <p role="alert" className="mt-4 text-[13.5px] text-ink-2">{DEVICE_REMOVAL_REMOTE_UNAVAILABLE_MESSAGE}</p>}
          <div className="mt-5 flex justify-end gap-2">
            <Dialog.Close asChild><Button variant="ghost" disabled={remove.isPending}>Cancel</Button></Dialog.Close>
            <Button variant="danger" disabled={remove.isPending} onClick={() => remove.mutate()}><KeyRound className="size-4" />{remove.isPending ? 'Waiting for your key…' : 'Remove with security key'}</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
